import { apiClient } from '@/lib/api'
import type { StandardResponse } from '@/types/api-response'
import { isErrorResponse } from '@/types/api-response'

export interface SystemMetrics {
  totalUsers: number
  totalTenants: number
  activeResources: number
  totalRevenue: number
  userGrowth: number
  tenantGrowth: number
  resourceGrowth: number
  revenueGrowth: number
}

export interface TenantOverview {
  id: string
  name: string
  status: string
  memberCount: number
  resourceCount: number
  balance: number
  createdAt: string
}

export interface RevenueMetrics {
  totalRevenue: number // Smallest currency unit (renamed from totalRevenueCents)
  revenueByPeriod: { period: string; revenue: number; transactionCount: number }[] // Smallest currency unit (renamed from revenueCents)
  topTenantsByRevenue: { tenantId: string; tenantName: string; totalRevenue: number }[] // Smallest currency unit (renamed from totalRevenueCents)
  generatedAt: string
  fromCache: boolean
}

export interface ResourceMetrics {
  totalResources: number
  resourcesByType: { type: string; count: number }[]
  resourcesByStatus: { status: string; count: number }[]
  generatedAt: string
  fromCache: boolean
}

export interface Activity {
  id: string
  type: string
  tenantId: string
  tenantName: string
  description: string
  status: string
  amount: number | null // Smallest currency unit (renamed from amountCents)
  timestamp: string
}

export interface RecentActivityResponse {
  activities: Activity[]
  generatedAt: string
  fromCache: boolean
}

export const adminDashboardService = {
  /**
   * Get system metrics
   * Returns: SystemMetrics (extracted from StandardResponse<SystemMetrics>)
   */
  async getSystemMetrics(): Promise<SystemMetrics> {
    const response = await apiClient.get<StandardResponse<SystemMetrics> | ErrorResponse>(
      '/api/v1/admin/dashboard/metrics'
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return response.data.data
  },

  /**
   * Get tenant overview
   * Returns: TenantOverview[] (extracted from StandardResponse<TenantOverview[]>)
   */
  async getTenantOverview(): Promise<TenantOverview[]> {
    const response = await apiClient.get<StandardResponse<TenantOverview[]> | ErrorResponse>(
      '/api/v1/admin/dashboard/tenant-overview'
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return response.data.data
  },

  /**
   * Get revenue metrics
   * Returns: RevenueMetrics (extracted from StandardResponse<RevenueMetrics>)
   */
  async getRevenueMetrics(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<RevenueMetrics> {
    const response = await apiClient.get<StandardResponse<RevenueMetrics> | ErrorResponse>(
      '/api/v1/admin/dashboard/revenue-metrics',
      {
        params: { period },
      }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return response.data.data
  },

  /**
   * Get resource metrics
   * Returns: ResourceMetrics (extracted from StandardResponse<ResourceMetrics>)
   */
  async getResourceMetrics(): Promise<ResourceMetrics> {
    const response = await apiClient.get<StandardResponse<ResourceMetrics> | ErrorResponse>(
      '/api/v1/admin/dashboard/resource-metrics'
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return response.data.data
  },

  /**
   * Get recent activity
   * Returns: Activity[] (extracted from StandardResponse<RecentActivityResponse>)
   */
  async getRecentActivity(limit: number = 10): Promise<Activity[]> {
    const response = await apiClient.get<StandardResponse<RecentActivityResponse> | ErrorResponse>(
      '/api/v1/admin/dashboard/recent-activity',
      {
        params: { limit },
      }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    // Extract activities array from the response
    return response.data.data.activities
  },
}
